import React, { useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CameraIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { PROFILE_ROUTE } from "../../../routes";
import useGetProfile from "../hooks/useGetProfile";
import {
  isProfilePhotoBannerDismissed,
  dismissProfilePhotoBanner,
} from "../utils/profilePhotoNudgeStorage";
import {
  trackProfilePhotoNudgeShown,
  trackProfilePhotoNudgeDismissed,
} from "../../../services/analytics";

const TOUCHPOINT = "banner";

/**
 * Persistent, dismissible banner nudging users still on the default
 * Gravatar to upload a real photo. Fixed (not sticky) below the header —
 * PublicLayout's root wrapper sets `overflow-x-hidden`, and per the CSS
 * spec, pairing an explicit `overflow-x` with an unset `overflow-y` forces
 * the latter to compute as `auto`, which silently turns that wrapper into a
 * non-scrolling "scroll container" that breaks `position: sticky` inside it
 * (it never actually scrolls, so nothing ever triggers the sticky offset).
 * `position: fixed` is immune to ancestor overflow, which is also why
 * AdvertisedEventBanner and the main Header use `fixed` here instead.
 *
 * It measures its own height into the `--profile-photo-banner-height` CSS
 * var (see index.css), which feeds into `--events-header-top` alongside
 * `--banner-height` — without this, the events page's `position: fixed`
 * filter bar (`.events-filter-fixed`, pinned to `--events-header-top`)
 * doesn't know to shift down for this banner and renders on top of it.
 * PublicLayout also renders a matching spacer of this same height so
 * in-flow page content isn't hidden underneath this fixed banner.
 *
 * The outer wrapper itself carries the opaque background and the full
 * padding (not just the inner orange card) so its whole measured box is
 * covered — otherwise the padding above/below the card is transparent and
 * scrolling page content visibly bleeds through that gap between this
 * banner and the header/filter bar above and below it.
 */
const ProfilePhotoBanner: React.FC = () => {
  const { user } = useGetProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => isProfilePhotoBannerDismissed());
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const isVisible = !!user && !user.hasCustomProfileImage && !dismissed;

  useLayoutEffect(() => {
    const clearHeightVar = () => {
      try {
        document.documentElement.style.setProperty("--profile-photo-banner-height", "0px");
      } catch {
        // ignore
      }
    };

    if (!isVisible) {
      clearHeightVar();
      return;
    }

    const el = wrapperRef.current;
    if (!el) return;

    const setHeightVar = () => {
      try {
        const h = el.getBoundingClientRect().height;
        document.documentElement.style.setProperty(
          "--profile-photo-banner-height",
          `${Math.round(h)}px`
        );
      } catch {
        // ignore
      }
    };

    setHeightVar();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => setHeightVar());
      try {
        ro.observe(el);
      } catch {
        // ignore
      }
    } else {
      window.addEventListener("resize", setHeightVar);
    }

    return () => {
      clearHeightVar();
      if (ro) {
        try {
          ro.disconnect();
        } catch {
          // ignore
        }
      } else {
        window.removeEventListener("resize", setHeightVar);
      }
    };
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const handleUploadClick = () => {
    trackProfilePhotoNudgeShown(TOUCHPOINT);
    navigate(
      `${PROFILE_ROUTE}?edit=true&redirectTo=${encodeURIComponent(location.pathname)}`
    );
  };

  const handleDismiss = () => {
    trackProfilePhotoNudgeDismissed(TOUCHPOINT);
    dismissProfilePhotoBanner();
    setDismissed(true);
  };

  return (
    <div
      className="fixed left-0 right-0 z-[35] bg-white"
      style={{ top: "calc(var(--main-header-height) + var(--banner-height))" }}
    >
      <div ref={wrapperRef} className="px-4 sm:px-6 lg:px-8 py-2">
        <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 flex items-center gap-3 shadow-sm">
          <div className="flex-shrink-0 bg-orange-100 rounded-full p-2">
            <CameraIcon className="h-5 w-5 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              Una foto per il tuo profilo?
            </p>
            <p className="text-xs text-gray-600">
              Aiuta le altre persone a riconoscerti agli eventi di GioJoy.
            </p>
          </div>
          <button
            onClick={handleUploadClick}
            className="flex-shrink-0 inline-flex items-center px-3 py-1.5 bg-orange-500 text-white text-xs font-semibold rounded-md hover:bg-orange-600 transition-colors duration-200"
          >
            Carica foto
          </button>
          <button
            onClick={handleDismiss}
            aria-label="Chiudi"
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePhotoBanner;
